import axios from 'axios';
import download from 'downloadjs';
import { BASE_URL } from '../../../../config/environmentConfig';
import { merchantActionTypes } from '../action-types';
import { MERCHANT_ROUTES, ON_BOARDING_ROUTES } from '../../../utils/constatns';
import {
	IAction,
	IFees,
	IFetchMerchantsListReportParams, IMerchantPaginationData,
	paramsTypes,
} from '../merchant-interfaces';
import { feesList } from '../merchant-context-variables';
import { sendSentryMessage } from '../../../utils/sentry-helper';
import * as Sentry from '@sentry/react';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import * as React from 'react';
import { IMerchantData } from '../../../interfaces/merchant';
import { ISelectorData } from '../../../components/application/NewApplication';
import { GET, POST, PUT } from '../../store-helper';
import { ISetDocuments } from './application';
import { IMerchantsFilters } from '../../../components/merchants/MerchantsListFilters';

export const fetchMerchantsLists = async (dispatch: React.Dispatch<IAction>): Promise<void> => {
	try {
		const data = await GET({
			pathname: `${BASE_URL.MERCHANTS}/lists`,
		});

		const ddaType = [
			{
				value: 'Checking',
				label: 'Checking'
			},
			{
				value: 'Savings',
				label: 'Savings'
			}
		];

		const lists = {
			ddaType,
			countries: [],
			currencies: [],
			states: [],
			businessTypes: [],
			ecommercePlatforms: [],
			industries: [],
			applicationStatuses: [],
			merchantStatuses: [],
			partnerTypes: [],
			cardBrands: [],
			productSoldFields: [],
			principalTypes: [],
			individualFormOfID: [],
			consumerNotificationChannelValues: [],
			feeTypes: [],
			batchTypes: [],
			batchTypes_v2: [],
			integrationTiers: [],
			interchanges: [],
			applicationOperationsStatuses: [],
			applicationRiskStatuses: [],

			acquisitions: [],
			averageChargebackRate: [],
			averageDeclineRate: [],
			averageDisputeRate: [],
			averageProducts: [],
			averageReturnRate: [],
			averageSales: [],
			averageTransactionAmount: [],
			maxSubDuration: [],
			trialPeriod: [],
			urlsRange: [],
			timeInBusiness: [],
			riskCategories: [],
		};
		const merchantLists = [
			{
				name: 'countries',
				value: 'twoLetterCode' // Do not change to threeLetterCode, it will break the application
			},
			{
				name: 'currencies',
				value: 'code'
			},
			{
				name: 'states',
				value: 'abbreviation'
			},
			{ name: 'businessTypes' },
			{ name: 'ecommercePlatforms' },
			{ name: 'industries' },
			{ name: 'applicationStatuses' },
			{ name: 'cardBrands' },
			{ name: 'merchantStatuses' },
			{ name: 'partnerTypes' },
			{ name: 'integrationTypes' },
			{ name: 'productSoldFields' },
			{ name: 'principalTypes' },
			{ name: 'individualFormOfID' },
			{ name: 'feeTypes' },
			{ name: 'consumerNotificationChannelValues' },
			{ name: 'batchTypes' },
			{ name: 'batchTypes_v2' },
			{ name: 'integrationTiers' },
			{ name: 'interchanges' },
			{ name: 'applicationOperationsStatuses' },
			{ name: 'applicationRiskStatuses' },
			{ name: 'acquisitions' },
			{ name: 'averageChargebackRate' },
			{ name: 'averageDeclineRate' },
			{ name: 'averageDisputeRate' },
			{ name: 'averageProducts' },
			{ name: 'averageReturnRate' },
			{ name: 'averageSales' },
			{ name: 'averageTransactionAmount' },
			{ name: 'maxSubDuration' },
			{ name: 'trialPeriod' },
			{ name: 'urlsRange' },
			{ name: 'timeInBusiness' },
			{ name: 'riskCategories' },
		];

		merchantLists.map(item => {
			if (data?.[item.name] && Array.isArray(data?.[item.name])) {
				lists[item.name] = data[item.name]?.map(el => ({
					value: item?.value ? el[item.value] : el,
					label: item?.value ? `${el.name}, ${el[item.value]}` : el

				}));
			} else if (item.name === 'batchTypes_v2') {
				lists[item.name] = Object.values(data?.[item.name] ?? {})?.map((el: any) => ({
					value: el?.label,
					label: el?.label,
					description: el?.description,
				}));

			} else {
				lists[item.name] = Object.entries(data?.[item.name] ?? {})?.map(([key, val]) => ({
					value: typeof key === 'number' ? Number(key) : key,
					label: val
				}));
			}
		});

		dispatch({ type: merchantActionTypes.FETCH_MERCHANTS_LISTS, payload: lists });
	} catch (e) {
		console.log('fetchMerchantsLists error', e);
		sendSentryMessage({ name: `GET Fetch merchant list error`, context: e });
		dispatch({ type: merchantActionTypes.FETCH_FAIL });
	}
};

export const fetchMerchantData = async (dispatch: React.Dispatch<IAction>, id: string): Promise<void> => {
	try {
		const data = await GET({ pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${id}` });
		let merchantData;
		if (data) {
			merchantData = data;
			const feesData = data?.fees;

			const fees: IFees[] = feesList(feesData);

			const feesHistory: any = Object.entries(data?.feesHistory || {});
			const newHistory = [];
			feesHistory?.forEach(([date, feesArr]) => {
				feesArr?.forEach(el => {
					newHistory.push({
						date,
						name: el?.name,
						amount: el?.amount / 100,
						id: el?.id
					});
				});
			});
			dispatch({ type: merchantActionTypes.SET_MERCHANT_FEES, payload: fees });
			dispatch({ type: merchantActionTypes.FETCH_MERCHANT_FEE_HISTORY, payload: newHistory });
		}
		dispatch({ type: merchantActionTypes.FETCH_MERCHANT_DATA, payload: merchantData });
	} catch (e) {
		Sentry.setTag('fetch-merchant-data-id', id);
		sendSentryMessage({ name: `GET Fetch merchant data`, context: e });
		dispatch({ type: merchantActionTypes.FETCH_FAIL });
	}
};

export const setMerchantDetailsDataDefault = (dispatch: React.Dispatch<IAction>): void => {
	dispatch({ type: merchantActionTypes.SET_MERCHANT_DETAILS_DATA_DEFAULT });
};

export const setStatus = async (dispatch: React.Dispatch<IAction>, id: string, data: IMerchantData): Promise<void> => {
	if (data?.status) {
		try {
			const path = data.status === 1 ? 'deactivate' : 'activate';
			await axios.post(`${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${id}/${path}`);
			await fetchMerchantData(dispatch, id);
		} catch (e) {
			Sentry.setTag('set-status-id', id);
			sendSentryMessage({ name: `POST Set status error`, context: e });
			console.log(e);
			dispatch({ type: merchantActionTypes.FETCH_FAIL });
			throw new Error(e);
		}
	}
};

export const fetchMerchants = async (searchParams: IMerchantsFilters): Promise<IMerchantPaginationData> => {
	try {

		return await GET({
			pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}`,
			params: searchParams
		});

	} catch (e) {
		sendSentryMessage({ name: `GET Fetch merchants error`, context: e });
		console.log(e);
	}
};

export const exportMerchants = async (searchParams: IMerchantsFilters): Promise<void> => {
	try {

		const res = await GET({
			pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/export`,
			params: searchParams
		});

		if (res) {
			const blob = new Blob([res], { type: 'text/csv' });
			download(blob, 'Merchants.csv');
		}

	} catch (e) {
		sendSentryMessage({ name: `GET Fetch merchants error`, context: e });
		console.log(e);
	}
};

export const fetchMerchantsListReport = async (
	dispatch: React.Dispatch<IAction>,
	page: number,
	merchantsList: ISelectorData[],
	searchValue: string
): Promise<void> => {
	try {
		let params: IFetchMerchantsListReportParams = {
			pageNumber: page,
			pageSize: 20
		};
		if (searchValue) {
			params = {
				...params,
				q: searchValue
			};
		}

		const data = await GET({
			pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}`,
			params
		});

		let merchants = [];

		let list;

		if (data?.rows && data?.rows.length) {
			list = data;
			const activeMerchants = data.rows?.filter(el => el.status === 1);
			merchants = activeMerchants?.map(el => ({
				value: el.id,
				label: el.companyName
			}));
		}

		const newMerchantsList = page !== 1 ? merchantsList.concat(merchants) : merchants;

		dispatch({ type: merchantActionTypes.FETCH_MERCHANTS_LIST_REPORT, payload: newMerchantsList });
		dispatch({ type: merchantActionTypes.FETCH_MERCHANTS_LIST_REPORT_DATA, payload: list });
	} catch (e) {
		console.log(e);
		dispatch({ type: merchantActionTypes.FETCH_FAIL });
	}
};

export const fetchListOfMerchants = async (dispatch: React.Dispatch<IAction>): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.START_LIST_OF_MERCHANTS_LOADING });

		const data = await GET({ pathname: `${BASE_URL.MERCHANTS}/Merchant/list` });

		let list = [];
		if (data) {
			list = data?.map(el => ({
				value: el?.id,
				label: el?.name?.length > 0 ? el?.name : el?.id,
				role: el?.isActive ? 'Active' : 'Not active',
				companyName: el?.companyName
			}));
		}
		const activeUser = list?.filter(el => el?.role === 'Active');
		const inactiveUser = list?.filter(el => el?.role === 'Not active');
		dispatch({ type: merchantActionTypes.FETCH_LIST_OF_MERCHANTS, payload: activeUser.concat(inactiveUser) });
		// dispatch({ type: merchantActionTypes.FETCH_LIST_OF_MERCHANTS, payload: list });

		const merchants = {};

		list?.forEach((list) => {
			// console.log('merchantNames', list);
			if (list.value) {
				merchants[list.value] = list.label;
			}
		});

		dispatch({ type: merchantActionTypes.SET_MERCHANT_NAMES, payload: merchants });
	} catch (e) {
		sendSentryMessage({ name: `POST Fetch list of merchant error`, context: e });
		console.log(e);
		dispatch({ type: merchantActionTypes.FETCH_FAIL });
	}
};

export const setChosenMerchant = (dispatch: React.Dispatch<IAction>, merchantId): void => {
	dispatch({ type: merchantActionTypes.SET_CHOSEN_MERCHANT, payload: merchantId });
};

export const setMerchantDetailsData = async (mid: string, params: paramsTypes, pathName: string, formik?: any): Promise<void> => {
	return await PUT({
			pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${mid}/${pathName}`,
			body: params,
			formik
		},
	);
};
export const addOwnerData = async (mid: string, params: paramsTypes): Promise<void> => {
	return await POST({
		pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${mid}/${MERCHANTS_TABS.OWNER}`,
		body: params
	});
};

export const deleteOwnerData = async (dispatch, mid: string, ownerId: string): Promise<void> => {
	try {
		await axios.delete(`${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${mid}/${MERCHANTS_TABS.OWNER}/${ownerId}`);
		await fetchMerchantData(dispatch, mid);
	} catch (e) {
		console.log('deleteOwnerData error', e);
	}
};

export const setMerchantDetailsPartnerConfigurationData = async (id: string, params: paramsTypes): Promise<void> => {
	return await PUT({
		pathname: `${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${id}/partners-general`,
		body: params
	});
};

export const fetchMccList = async (dispatch: React.Dispatch<IAction>): Promise<void> => {
	try {
		const data = await GET({
			pathname: `${BASE_URL.MERCHANTS}/${ON_BOARDING_ROUTES.MCC}`,
		});
		if (data && data?.length) {
			const mccList = data?.map(el => ({
				value: el?.code,
				label: `${el?.code}, ${el?.category}, ${el?.description}`
			}));
			dispatch({ type: merchantActionTypes.FETCH_MCC_LIST, payload: mccList });
		}
		} catch (e) {
		sendSentryMessage({ name: `GET Fetch mcc list error`, context: e });
		console.log(e);
	}
};

export const setMerchantDocuments = async ({ id, fileList, uploadedFiles }: ISetDocuments): Promise<void> => {
	const formData = new FormData();
	fileList.forEach((file) => {
		formData.append(`file_${file?.name}`, file?.blobFile);
	});

	const params = {
		documents: uploadedFiles || [],
	};
	formData.append('documents', JSON.stringify(params));
	// formData.append('documents', JSON.stringify({ documents: params.documents }));

	const res = await axios.put(`${BASE_URL.MERCHANTS}/${MERCHANT_ROUTES.MERCHANT}/${id}/documents`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});
	console.log('res', res);
};
